import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { eventBus } from '@/utils/eventBus';
import { log } from '@/utils/vueLogger.js';
import { setLocale, normalizeLocale } from './index';
import { pushTranslationsToIframe } from './legacyBridge';
import { getApplicationResources } from '@/api/core/resource';

// Resolve the single active locale, mirroring legacy getLocalLanguage():
// connected -> user.locale ; otherwise loginLocale / browser language.
function resolveActiveLocale() {
	const user = iframeDataBridge.getCurrentUser();
	if (user?.locale) return normalizeLocale(user.locale);

	const login = localStorage.getItem('loginLocale');
	if (login) return normalizeLocale(login);

	return normalizeLocale(navigator.language);
}

let _resHookInstalled = false;

async function loadResourcesIfConnected(locale, force = false) {
	const user = iframeDataBridge.getCurrentUser();
	if (!user?.id) return; // not connected -> base i18n only

	try {
		// applyTranslations() runs inside getApplicationResources (merge + iframe push).
		await getApplicationResources(locale, force);
	} catch (e) {
		log('error', 'i18n', `Failed to load application resources for ${locale}: ${e.message}`);
	}

	// The per-app tokens (tokenSales/tokenDoc) are only set after each app logon, which
	// happens after the first load. Re-fetch (with all then-available tokens) when an app
	// announces it is ready, so app-scoped W- translations are picked up.
	if (!_resHookInstalled) {
		_resHookInstalled = true;
		const refetch = () => loadResourcesIfConnected(locale, true);
		// evtSynchroEnd is published by the legacy iframe's amplify, not the parent window's.
		// iframeWatcher aliases window.amplify → iframe.amplify once ready, so this is safe.
		try {
			const iframeAmplify = iframeDataBridge.getIframeWindow()?.amplify;
			if (iframeAmplify?.subscribe) iframeAmplify.subscribe('evtSynchroEnd', refetch);
		} catch (e) {
			/* amplify not ready */
		}
		eventBus.on('main-sync-completed', refetch);
	}
}

/**
 * Initialize the single active language for Vue and the legacy iframe.
 * Loads only one locale's base messages, then (once connected) overlays the
 * custom applicationResources for that same locale.
 */
export async function initI18n() {
	const locale = resolveActiveLocale();
	await setLocale(locale);
	pushTranslationsToIframe(locale);

	const reconcile = async () => {
		const real = resolveActiveLocale();
		if (real !== locale) {
			await setLocale(real);
			pushTranslationsToIframe(real);
		}
		await loadResourcesIfConnected(real);
	};

	// Three complementary triggers, because the resources must be (re)fetched whenever the app
	// becomes connected or the iframe reloads:
	//  - externis-ready: fresh iframe document load (full page reload while a session is valid).
	//  - view-changed:   internal KO navigation that publishes evtChangeView (app switches, etc.).
	//  - user-changed:   login (login.js) / SSO (Startup.js). The login → home transition sets
	//    ecosVM.currentView('greeter') DIRECTLY — it does NOT publish evtChangeView and stays in
	//    the same document — so without this, reconcile would never run after a fresh login and
	//    the applicationResources overlay would never be fetched.
	eventBus.on('externis-ready', reconcile);
	eventBus.on('view-changed', reconcile);
	eventBus.on('user-changed', reconcile);
	if (iframeDataBridge.getIframeWindow()?.externis) reconcile();
}
